import { useEffect, useRef } from "react"
import type { StudioGuideItem } from "@/lib/types"

interface PhonePreviewProps {
  items: StudioGuideItem[]
  selectedItemId: number | null
  onSelectItem: (id: number) => void
}

export function PhonePreview({ items, selectedItemId, onSelectItem }: PhonePreviewProps) {
  const cardRefs = useRef<Map<number, HTMLDivElement>>(new Map())

  useEffect(() => {
    if (selectedItemId === null) return
    const el = cardRefs.current.get(selectedItemId)
    if (el) {
      el.scrollIntoView({ behavior: "smooth", block: "nearest" })
    }
  }, [selectedItemId])

  const poiItems = items.filter((item) => item.poi !== null)

  return (
    <div
      className="w-[280px] shrink-0 rounded-2xl border overflow-hidden"
      style={{ backgroundColor: "#FBF7F1", maxHeight: "540px", overflowY: "auto" }}
    >
      {/* Fake iPhone status bar */}
      <div
        className="flex h-6 items-center justify-center"
        style={{ backgroundColor: "#FBF7F1" }}
      >
        <span className="text-[10px]" style={{ color: "#6B6256" }}>
          9:41
        </span>
      </div>

      {/* Header */}
      <div className="px-4 pb-2 pt-1">
        <h2 className="font-serif text-lg" style={{ color: "#1F1A14" }}>
          Your guide
        </h2>
      </div>

      {/* POI cards */}
      <div className="pb-4">
        {poiItems.length === 0 ? (
          <div className="px-4 py-8 text-center">
            <p className="text-xs" style={{ color: "#A8957A" }}>
              No items in guide yet
            </p>
          </div>
        ) : (
          poiItems.map((item) => {
            const poi = item.poi!
            const isSelected = item.id === selectedItemId

            return (
              <div
                key={item.id}
                ref={(el) => {
                  if (el) cardRefs.current.set(item.id, el)
                  else cardRefs.current.delete(item.id)
                }}
                onClick={() => onSelectItem(item.id)}
                className={[
                  "mx-3 mb-2 cursor-pointer rounded-lg bg-white p-3 shadow-sm",
                  item.is_highlighted ? "border-l-2" : "",
                  isSelected ? "ring-2" : "",
                ].join(" ")}
                style={{
                  borderLeftColor: item.is_highlighted ? "#C2553A" : undefined,
                  outline: isSelected ? "2px solid #C2553A" : undefined,
                  outlineOffset: isSelected ? "0px" : undefined,
                }}
              >
                {/* Cover image or placeholder */}
                {poi.cover_url ? (
                  <img
                    src={poi.cover_url}
                    alt={poi.name}
                    className="mb-2 h-24 w-full rounded-md object-cover"
                  />
                ) : (
                  <div
                    className="mb-2 h-24 w-full rounded-md"
                    style={{ backgroundColor: "#E8DFD0" }}
                  />
                )}

                {/* POI name */}
                <p className="font-serif text-sm font-medium" style={{ color: "#1F1A14" }}>
                  {poi.name}
                </p>

                {/* Category */}
                {poi.categories[0] && (
                  <p
                    className="mt-0.5 text-[10px] uppercase tracking-wider"
                    style={{ color: "#A8957A" }}
                  >
                    {poi.categories[0].name}
                  </p>
                )}

                {/* Host note */}
                {item.host_note && (
                  <p
                    className="mt-1 line-clamp-2 text-[11px] italic"
                    style={{ color: "#6B6256" }}
                  >
                    {item.host_note}
                  </p>
                )}
              </div>
            )
          })
        )}
      </div>
    </div>
  )
}
